/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useRef, useEffect, useCallback, memo } from 'react';
import * as nifti from 'nifti-reader-js';
import {
  Box,
  Slider,
  Typography,
  Paper,
  Button,
  IconButton,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  CircularProgress,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import styled from '@emotion/styled';
import {
  convert_to_nifit_file_reader_data,
  niftiPointToDisplay,
  normalizePoint,
  volumeFromNiftiImage,
} from './helpers';
import {
  distanceToLineSegment,
  isNearPolygonEdgesOrVertices,
  VERTEX_HIT_RADIUS,
  EDGE_HIT_THRESHOLD,
  SAM_UNLABELED_COLOR,
  segmentsToSamPreviewPolygons,
  isUnlabeledPolygon,
} from './SegmentationNewLabellingPage/helpers';
import {
  checkSamNiftiStatus,
  initiateSamNifti,
  performSamNifti,
} from '@services/samService';
import services from '../../../services';
import { showErrorSnackbar, showSuccessSnacbar } from '@components/shared-layouts/Snackbar/helper';

const SAM_POLL_INTERVAL_MS = 10000;

const LABEL_LIST_SX = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
  maxHeight: 280,
  overflowY: 'auto',
};

interface Point {
  x: number;
  y: number;
}

interface Polygon {
  points: Point[];
  label: string;
  color: string;
}

interface SegmentationData {
  [sliceIndex: number]: {
    polygons: Polygon[];
    activePolygon: Point[];
  };
}

interface Label {
  name: string;
  color: string;
}

const EMPTY_LABELS: Label[] = [];

interface NiftiFileReaderProps {
  initialNiftiData?: ArrayBuffer;
  projectId?: string | number;
  imageId?: string;
  labelsFromContext?: Label[];
  preLoadedAnnotations?: any;
  onSave?: (segmentationData: SegmentationData, imageDimensions: ImageDimensions | null) => Promise<void>;
  hideSaveButton?: boolean;
  saveTrigger?: number; // When this changes, trigger save
  externalSliceIndex?: number; // External control of slice index
  showSamToggle?: boolean;
}

// Add new interface for dimensions
interface ImageDimensions {
  width: number;
  height: number;
  totalSlices: number;
}

const ViewerContainer = styled(Paper)`
  padding: 16px;
  margin-top: 16px;
  position: relative;
  background: #fff;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ControlsContainer = styled(Box)`
  display: flex;
  align-items: center;
  padding: 8px 16px;
  background: #f8f9fa;
  border-radius: 8px;
  gap: 24px;

  .control-group {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
    max-width: 400px;
  }
`;

const ViewerWrapper = styled(Box)`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CanvasContainer = styled(Box)`
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  overflow: hidden;
  position: relative;

  canvas {
    /* Keep aspect ratio and allow proper interaction */
    max-width: 100%;
    max-height: 100%;
    width: auto !important;
    height: auto !important;
    object-fit: contain;
  }
`;

const SliceControls = styled(Box)`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 16px;
  background: #f8f9fa;
  border-radius: 8px;

  .MuiTypography-root {
    font-size: 14px;
    white-space: nowrap;
  }
`;

const StyledSlider = styled(Slider)`
  .MuiSlider-rail {
    background-color: #ed8623a1;
  }
  .MuiSlider-track {
    background-color: #f04a23;
  }
  .MuiSlider-thumb {
    background-color: #f04a23;
    &:hover, &.Mui-focusVisible {
      box-shadow: 0 0 0 8px rgba(240, 74, 35, 0.16);
    }
  }
`;

const SaveButton = styled(Button)`
  background: linear-gradient(to right, #f04a23, #ed8623a1);
  color: white;
  &:hover {
    background: linear-gradient(to right, #d43d1a, #d67219);
  }
`;

const WelcomeContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100% !important;
  padding: 20px;
  text-align: center;
  color: #666;
  position: relative;
`;

const WelcomeContent = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  max-width: 600px;
  margin: 0 auto;
`;

const WelcomeIcon = styled.div`
  font-size: 48px;
  color: #f04a23;
  margin-bottom: 20px;
  
  &::before {
    content: '';
    display: block;
    width: 64px;
    height: 64px;
    background: #f04a23;
    mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z'/%3E%3C/svg%3E") center/contain no-repeat;
  }
`;

const WelcomeTitle = styled(Typography)`
  color: #f04a23;
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 8px;
`;

const WelcomeText = styled(Typography)`
  font-size: 16px;
  line-height: 1.6;
  color: #666;
  max-width: 500px;
  text-align: center;
`;

function NiftiFileReader({ initialNiftiData, projectId, imageId, labelsFromContext, preLoadedAnnotations, onSave, hideSaveButton = false, saveTrigger, externalSliceIndex, showSamToggle = false }: NiftiFileReaderProps) {
  
  console.log("projectId from NiftiFileReader ===>", projectId);
  console.log("imageId from NiftiFileReader ===>", imageId);
  console.log("[NiftiFileReader] preLoadedAnnotations received:", { 
    hasAnnotations: !!preLoadedAnnotations,
    type: typeof preLoadedAnnotations,
    isObject: preLoadedAnnotations && typeof preLoadedAnnotations === 'object',
    keys: preLoadedAnnotations ? Object.keys(preLoadedAnnotations) : [],
    sampleSlice: preLoadedAnnotations ? Object.values(preLoadedAnnotations)[0] : null
  });
  const [niftiHeader, setNiftiHeader] = useState<any>(null);
  const [niftiImage, setNiftiImage] = useState<any>(null);
  const [error, setError] = useState<string>('');
  const [currentSlice, setCurrentSlice] = useState<number>(0);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [dataRange, setDataRange] = useState<{ min: number; max: number } | null>(null);
  const [segmentationData, setSegmentationData] = useState<SegmentationData>({});
  const [activeLabel, setActiveLabel] = useState<string>('');
  const labels = labelsFromContext ?? EMPTY_LABELS;
  // const [isDrawingPolygon, setIsDrawingPolygon] = useState(false);
  const [selectedPoint] = useState<{polygonIndex: number, pointIndex: number} | null>(null);
  const [isDrawing] = useState(false);
  const [brushSize] = useState(5);
  const [tool] = useState<'brush' | 'eraser'>('brush');
  
  const segmentationCanvasRef = useRef<HTMLCanvasElement>(null);
  const lastPos = useRef<{ x: number; y: number } | null>(null);
  const suppressEditModeExitClickRef = useRef(false);
  const [selectedPolygon, setSelectedPolygon] = useState<number | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isPolygonClosed, setIsPolygonClosed] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragPoint, setDragPoint] = useState<{ index: number, initialPos: Point } | null>(null);

  // Add a new state for the temporary polygon that needs a label
  const [pendingPolygon, setPendingPolygon] = useState<Point[] | null>(null);

  // Add state for label editing
  const [editingLabel, setEditingLabel] = useState<{ polygonIndex: number, position: { x: number, y: number } } | null>(null);

  const dialogRef = useRef<HTMLDivElement>(null);
  const samPollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [enableSam, setEnableSam] = useState(false);
  const [samToggleLoading, setSamToggleLoading] = useState(false);
  const [samPreviewBySlice, setSamPreviewBySlice] = useState<Record<number, Polygon[]>>({});
  const [samLoading, setSamLoading] = useState(false);
  const [samInitDialogOpen, setSamInitDialogOpen] = useState(false);
  const [samInitPercentage, setSamInitPercentage] = useState(0);
  const [samInitMessage, setSamInitMessage] = useState(
    'We are initializing SAM for this volume. Please wait.'
  );
  const [labelQueueIndices, setLabelQueueIndices] = useState<number[]>([]);
  const [labelQueuePosition, setLabelQueuePosition] = useState(0);

  const clearSamPollInterval = useCallback(() => {
    if (samPollIntervalRef.current) {
      clearInterval(samPollIntervalRef.current);
      samPollIntervalRef.current = null;
    }
  }, []);

  const openSamInitDialog = useCallback((message?: string, percentage?: number) => {
    setSamInitMessage(
      message ?? 'We are initializing SAM for this volume. Please wait.'
    );
    setSamInitPercentage(percentage ?? 0);
    setSamInitDialogOpen(true);
  }, []);

  const closeSamInitDialog = useCallback(() => {
    clearSamPollInterval();
    setSamInitDialogOpen(false);
  }, [clearSamPollInterval]);

  const pollSamInitStatus = useCallback(async () => {
    if (!projectId || !imageId) return;

    try {
      const result = await checkSamNiftiStatus(projectId, imageId);
      if (result.status !== 200 || !result.data) return;

      const { message, percentage } = result.data;
      setSamInitMessage(message);
      setSamInitPercentage(percentage);

      if (percentage >= 100) {
        closeSamInitDialog();
        showSuccessSnacbar('SAM is ready. Turn on Enable SAM to use it.');
      }
    } catch (err) {
      console.error('SAM status poll error:', err);
    }
  }, [projectId, imageId, closeSamInitDialog]);

  useEffect(() => {
    if (!showSamToggle || !samInitDialogOpen || !projectId || !imageId) {
      clearSamPollInterval();
      return;
    }

    pollSamInitStatus();
    samPollIntervalRef.current = setInterval(pollSamInitStatus, SAM_POLL_INTERVAL_MS);

    return () => clearSamPollInterval();
  }, [showSamToggle, samInitDialogOpen, projectId, imageId, pollSamInitStatus, clearSamPollInterval]);

  useEffect(() => {
    setSamPreviewBySlice({});
    setEnableSam(false);
  }, [imageId]);

  const clearDrawingState = useCallback(() => {
    setIsEditing(false);
    setSelectedPolygon(null);
    setPendingPolygon(null);
    setIsPolygonClosed(false);
    setEditingLabel(null);
    setSegmentationData((prev) => ({
      ...prev,
      [currentSlice]: {
        ...prev[currentSlice],
        activePolygon: [],
      },
    }));
  }, [currentSlice]);

  const handleSamToggle = async (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    if (!showSamToggle || !projectId || !imageId || samToggleLoading) return;

    if (!checked) {
      let mergedCurrentSlicePolygons: Polygon[] = [
        ...(segmentationData[currentSlice]?.polygons ?? []),
        ...(samPreviewBySlice[currentSlice] ?? []),
      ];

      setSegmentationData((prev) => {
        const updated = { ...prev };
        for (const [sliceKey, previews] of Object.entries(samPreviewBySlice)) {
          if (!previews.length) continue;
          const slice = Number(sliceKey);
          const currentPolygons = updated[slice]?.polygons ?? [];
          const merged = [...currentPolygons, ...previews];
          updated[slice] = {
            ...updated[slice],
            polygons: merged,
            activePolygon: [],
          };
          if (slice === currentSlice) {
            mergedCurrentSlicePolygons = merged;
          }
        }
        return updated;
      });

      setSamPreviewBySlice({});
      setEnableSam(false);

      const unlabeledIndices = mergedCurrentSlicePolygons
        .map((p, i) => (isUnlabeledPolygon(p) ? i : -1))
        .filter((i) => i >= 0);

      if (unlabeledIndices.length > 0) {
        setLabelQueueIndices(unlabeledIndices);
        setLabelQueuePosition(0);
      }
      return;
    }

    setSamToggleLoading(true);
    try {
      const result = await checkSamNiftiStatus(projectId, imageId);

      if (result.status === 200 && result.data) {
        const { message, percentage } = result.data;
        if (percentage >= 100) {
          clearDrawingState();
          setEnableSam(true);
        } else {
          openSamInitDialog(message, percentage);
        }
      } else if (result.status === 400) {
        await initiateSamNifti(projectId, imageId);
        openSamInitDialog();
      }
    } catch (err) {
      console.error('SAM enable error:', err);
      const errorMessage =
        (err as { error?: { message?: string } })?.error?.message ||
        'Failed to enable SAM';
      showErrorSnackbar(errorMessage);
    } finally {
      setSamToggleLoading(false);
    }
  };

  const performSamSegmentation = useCallback(
    async (niftiCoords: Point) => {
      if (!projectId || !imageId || samLoading) return;

      setSamLoading(true);
      try {
        // Pointer coords are already imshow / API pixel space
        const response = await performSamNifti(
          projectId,
          imageId,
          currentSlice,
          Math.round(niftiCoords.x),
          Math.round(niftiCoords.y)
        );

        const rawPolygons = segmentsToSamPreviewPolygons(response.segments);
        if (!rawPolygons.length) {
          showErrorSnackbar('SAM did not return valid segments');
          return;
        }

        // Store in imshow pixel space; canvas draws 1:1
        const niftiPolygons = rawPolygons.map((polygon) => ({
          ...polygon,
          points: (polygon.points || []).map((p) => normalizePoint(p)),
        }));

        setSamPreviewBySlice((prev) => ({
          ...prev,
          [currentSlice]: [...(prev[currentSlice] ?? []), ...niftiPolygons],
        }));
      } catch (err) {
        console.error('perform-sam-nifti error:', err);
        const errorMessage =
          (err as { error?: { message?: string } })?.error?.message ||
          'Failed to perform SAM segmentation';
        showErrorSnackbar(errorMessage);
      } finally {
        setSamLoading(false);
      }
    },
    [projectId, imageId, currentSlice, samLoading]
  );

  const handleSamLabelQueueSelect = (labelName: string) => {
    if (labelQueueIndices.length === 0) return;

    const polygonIndex = labelQueueIndices[labelQueuePosition];
    const labelColor = labels.find((l) => l.name === labelName)?.color || '#FF0000';

    setSegmentationData((prev) => {
      const currentSliceData = prev[currentSlice];
      const newPolygons = [...currentSliceData.polygons];
      newPolygons[polygonIndex] = {
        ...newPolygons[polygonIndex],
        label: labelName,
        color: labelColor,
      };

      return {
        ...prev,
        [currentSlice]: {
          ...currentSliceData,
          polygons: newPolygons,
        },
      };
    });

    const nextPosition = labelQueuePosition + 1;
    if (nextPosition >= labelQueueIndices.length) {
      setLabelQueueIndices([]);
      setLabelQueuePosition(0);
    } else {
      setLabelQueuePosition(nextPosition);
    }
  };

  // Add new state for dimensions
  const [imageDimensions, setImageDimensions] = useState<ImageDimensions | null>(null);
  
  // Add window/level controls (declare before renderSlice)
  const [windowWidth, setWindowWidth] = useState(255);
  const [windowCenter, setWindowCenter] = useState(128);
  
  // Ref to track if annotations have been converted to prevent infinite loops
  const annotationsConvertedRef = useRef<boolean>(false);
  const lastAnnotationsRef = useRef<any>(null);
  
  // Reset conversion flag when preLoadedAnnotations prop actually changes (by content, not reference)
  // Don't set lastAnnotationsRef here - let the conversion useEffects set it after conversion
  useEffect(() => {
    if (preLoadedAnnotations) {
      const annotationsKey = JSON.stringify(preLoadedAnnotations);
      if (lastAnnotationsRef.current !== annotationsKey) {
        console.log('[NiftiFileReader] Annotations prop changed (by content), resetting conversion flag', {
          oldKey: lastAnnotationsRef.current ? 'exists' : 'none',
          newKeyLength: annotationsKey.length
        });
        annotationsConvertedRef.current = false;
        // Don't set lastAnnotationsRef here - conversion will set it
      }
    } else {
      // Reset if annotations are cleared
      if (lastAnnotationsRef.current !== null) {
        console.log('[NiftiFileReader] Annotations cleared, resetting conversion flag');
        annotationsConvertedRef.current = false;
        lastAnnotationsRef.current = null;
      }
    }
  }, [preLoadedAnnotations]);
  
  // Convert preloaded annotations function - use ref to access niftiHeader to avoid dependency issues
  const convertPreloadedAnnotations = useCallback((annotations: any, header: any) => {
    const convertedData: SegmentationData = {};

    // Initialize all slices with empty data
    if (header && header.dims && header.dims[3]) {
      for (let i = 0; i < header.dims[3]; i++) {
        convertedData[i] = {
          polygons: [],
          activePolygon: []
        };
      }
    }

    // Add the preloaded annotations
    console.log('[convertPreloadedAnnotations] Converting annotations', {
      annotationKeys: Object.keys(annotations),
      totalSlices: header?.dims?.[3],
      sliceRange: header?.dims?.[3] ? `0-${header.dims[3] - 1}` : 'unknown'
    });
    
    Object.entries(annotations).forEach(([sliceIndex, sliceData]: [string, any]) => {
      if (Array.isArray(sliceData)) {
        const sliceNum = parseInt(sliceIndex, 10);
        if (isNaN(sliceNum)) {
          console.warn(`[convertPreloadedAnnotations] Invalid slice index: ${sliceIndex}, skipping`);
          return;
        }
        
        // Validate slice number is within bounds
        if (header?.dims?.[3] && (sliceNum < 0 || sliceNum >= header.dims[3])) {
          console.warn(`[convertPreloadedAnnotations] Slice ${sliceNum} out of bounds (0-${header.dims[3] - 1}), skipping`);
          return;
        }
        
        console.log(`[convertPreloadedAnnotations] Processing slice ${sliceNum}`, {
          polygonCount: sliceData.length,
          firstPolygon: sliceData[0] ? { 
            pointCount: sliceData[0].points?.length, 
            label: sliceData[0].label,
            color: sliceData[0].color,
            firstPoint: sliceData[0].points?.[0]
          } : null
        });
        
        const polygons = sliceData.map((polygon: any) => ({
          // Keep imshow / API pixel coords (x=col=j, y=row=i); canvas uses the same space
          points: (polygon.points || []).map((p: any) => normalizePoint(p)),
          label: polygon.label || 'unknown',
          color: polygon.color || '#FF0000'
        }));

        convertedData[sliceNum] = {
          polygons,
          activePolygon: []
        };
      } else {
        console.warn(`[convertPreloadedAnnotations] Slice ${sliceIndex} data is not an array:`, typeof sliceData);
      }
    });

    console.log('[convertPreloadedAnnotations] Conversion complete', {
      convertedSlices: Object.keys(convertedData).length,
      sliceIndices: Object.keys(convertedData).map(k => parseInt(k, 10)).sort((a, b) => a - b),
      sampleSlice62: convertedData[62] ? { polygonCount: convertedData[62].polygons.length } : 'not found',
      sampleSlice63: convertedData[63] ? { polygonCount: convertedData[63].polygons.length } : 'not found'
    });

    return convertedData;
  }, []);
  
  // Add this useEffect to handle initialNiftiData
  useEffect(() => {
    if (initialNiftiData) {
      try {
        console.log('=== NIFTI File Processing Start ===');
        console.log('Received NIFTI data size:', initialNiftiData.byteLength, 'bytes');
        
        if (initialNiftiData.byteLength < 348) {
          const errorMsg = `File too small (${initialNiftiData.byteLength} bytes). A valid NIFTI file should be at least 348 bytes (header size).`;
          console.error(errorMsg);
          setError(errorMsg);
          return;
        }
        
        let buffer: any = initialNiftiData;
        const isCompressed = nifti.isCompressed(buffer);
        console.log('Is compressed:', isCompressed);
        console.log('Buffer type:', buffer.constructor.name);
        console.log('Buffer byteLength:', buffer.byteLength);

        if (isCompressed) {
          console.log('Decompressing NIFTI file...');
          try {
            buffer = nifti.decompress(buffer);
            console.log('Decompressed buffer size:', buffer.byteLength, 'bytes');
          } catch (decompressErr) {
            const errorMsg = `Decompression failed: ${decompressErr instanceof Error ? decompressErr.message : 'Unknown error'}`;
            console.error(errorMsg, decompressErr);
            setError(errorMsg);
            return;
          }
        }

        const isValid = nifti.isNIFTI(buffer);
        console.log('Is valid NIFTI:', isValid);
        
        if (!isValid) {
          const errorMsg = 'Not a valid NIFTI file. Please ensure the file is a valid .nii or .nii.gz file.';
          console.error(errorMsg);
          setError(errorMsg);
          return;
        }
        
        console.log('Reading NIFTI header...');
        const header = nifti.readHeader(buffer);
        console.log('NIFTI Header:', header);
        console.log('Header dimensions:', header.dims);
        
        if (!header.dims || header.dims.length < 4) {
          const errorMsg = 'Invalid NIFTI header: missing or invalid dimensions';
          console.error(errorMsg);
          setError(errorMsg);
          return;
        }
        
        console.log('Reading NIFTI image data...');
        const image = nifti.readImage(header, buffer);
        console.log('Image buffer byteLength:', image.byteLength);
        
        // Storage-order Float32 with scl_slope/scl_inter (no affine reorientation)
        const imageData = volumeFromNiftiImage(
          image,
          header.datatypeCode,
          header.scl_slope,
          header.scl_inter
        );
        console.log('Image data created, length:', imageData.length);
        
        // Display size matches imshow(data[:, :, z]): width=ny, height=nx
        const nx = header.dims[1];
        const ny = header.dims[2];
        const nz = header.dims[3];
        const dimensions: ImageDimensions = {
          width: ny,
          height: nx,
          totalSlices: nz
        };
        
        console.log('Image dimensions (display):', dimensions, { nx, ny, nz });
        console.log('Expected total voxels:', nx * ny * nz);
        console.log('Actual image data length:', imageData.length);
        
        if (dimensions.width <= 0 || dimensions.height <= 0 || dimensions.totalSlices <= 0) {
          const errorMsg = `Invalid dimensions: ${dimensions.width}x${dimensions.height}x${dimensions.totalSlices}`;
          console.error(errorMsg);
          setError(errorMsg);
          return;
        }
        
        setNiftiHeader(header);
        setNiftiImage(imageData);
        setImageDimensions(dimensions);
        setCurrentSlice(0);
        setError('');
        
        console.log('=== NIFTI File Processing Complete ===');
        
        // Convert and set preloaded annotations if they exist
        // Only convert if annotations haven't been converted yet or if they've changed
        if (preLoadedAnnotations) {
          const annotationsKey = JSON.stringify(preLoadedAnnotations);
          if (!annotationsConvertedRef.current || lastAnnotationsRef.current !== annotationsKey) {
            console.log('[NiftiFileReader] Converting preloaded annotations during NIFTI processing', {
              hasAnnotations: !!preLoadedAnnotations,
              sliceCount: Object.keys(preLoadedAnnotations).length,
              alreadyConverted: annotationsConvertedRef.current,
              headerDims: header.dims
            });
            const convertedData = convertPreloadedAnnotations(preLoadedAnnotations, header);
            console.log('[NiftiFileReader] Annotations converted during processing', {
              convertedSliceCount: Object.keys(convertedData).length
            });
            setSegmentationData(convertedData);
            annotationsConvertedRef.current = true;
            lastAnnotationsRef.current = annotationsKey;
          } else {
            console.log('[NiftiFileReader] Annotations already converted, skipping');
          }
        } else {
          console.log('[NiftiFileReader] No preloaded annotations to convert during processing');
        }
      } catch (err) {
        const errorMsg = `Error processing NIFTI file: ${err instanceof Error ? err.message : 'Unknown error'}`;
        console.error('=== NIFTI File Processing Error ===');
        console.error(errorMsg, err);
        console.error('Error stack:', err instanceof Error ? err.stack : 'No stack trace');
        setError(errorMsg);
      }
    }
  }, [initialNiftiData, preLoadedAnnotations]);

  // Separate useEffect to handle annotations when they load after NIFTI file is already processed
  useEffect(() => {
    // Only convert if annotations exist, header is loaded, and we haven't converted these annotations yet
    if (preLoadedAnnotations && niftiHeader && niftiImage) {
      const annotationsKey = JSON.stringify(preLoadedAnnotations);
      if (!annotationsConvertedRef.current || lastAnnotationsRef.current !== annotationsKey) {
        console.log('[NiftiFileReader] Converting preloaded annotations after NIFTI file is loaded', { 
          hasAnnotations: !!preLoadedAnnotations,
          sliceCount: preLoadedAnnotations ? Object.keys(preLoadedAnnotations).length : 0,
          niftiHeaderDims: niftiHeader?.dims,
          alreadyConverted: annotationsConvertedRef.current
        });
        try {
          const convertedData = convertPreloadedAnnotations(preLoadedAnnotations, niftiHeader);
          console.log('[NiftiFileReader] Annotations converted successfully', { 
            convertedSliceCount: Object.keys(convertedData).length,
            sampleSlice: convertedData[0] || null,
            slice62: convertedData[62] ? { polygonCount: convertedData[62].polygons.length } : null,
            slice63: convertedData[63] ? { polygonCount: convertedData[63].polygons.length } : null
          });
          setSegmentationData(convertedData);
          annotationsConvertedRef.current = true;
          lastAnnotationsRef.current = annotationsKey;
        } catch (err) {
          console.error('[NiftiFileReader] Error converting preloaded annotations:', err);
        }
      } else {
        console.log('[NiftiFileReader] Annotations already converted, skipping conversion');
      }
    } else {
      console.log('[NiftiFileReader] Waiting for annotations/header/image', {
        hasAnnotations: !!preLoadedAnnotations,
        hasHeader: !!niftiHeader,
        hasImage: !!niftiImage
      });
    }
  }, [preLoadedAnnotations, niftiHeader, niftiImage]);

  const saveSegmentation = useCallback(async () => {
    console.log("segmentationData ===>", segmentationData);
    
    // If custom save handler is provided, use it
    // segmentationData is kept in NIfTI/API coordinate space
    if (onSave) {
      try {
        await onSave(segmentationData, imageDimensions);
      } catch (error) {
        console.error('Error in custom save handler:', error);
        showErrorSnackbar("Error saving segmentations");
      }
      return;
    }
    
    // Default save behavior
    const convertedSegmentationData = convert_to_nifit_file_reader_data(segmentationData as any);
    
    const segmentationOutput = {
      project_id: projectId,
      image_id: imageId,
      size: [imageDimensions?.height, imageDimensions?.width, imageDimensions?.totalSlices],
      annotations: convertedSegmentationData
    };
    
    console.log("segmentationOutput ===>", segmentationOutput);
    
    try {      
      const customBaseUrl = 'https://genie-be-3d-dev.avawatz.com/api'
      const response = await services.post('/genie-3d/ct-segmentation/data/annotation', segmentationOutput, customBaseUrl);
      console.log('Segmentation saved successfully', response);
      showSuccessSnacbar("Segmentations saved successfully");
    } catch (error) {
      console.error('Error saving segmentation:', error);
      showErrorSnackbar("Error saving segmentations");
    }
  }, [segmentationData, imageDimensions, onSave, projectId, imageId]);

  // Trigger save when saveTrigger changes
  useEffect(() => {
    if (saveTrigger !== undefined && saveTrigger > 0) {
      saveSegmentation();
    }
  }, [saveTrigger, saveSegmentation]);

  const renderSlice = useCallback(() => {
    if (!niftiHeader || !niftiImage || !canvasRef.current) {
      console.log('Cannot render: missing data', { 
        hasHeader: !!niftiHeader, 
        hasImage: !!niftiImage, 
        hasCanvas: !!canvasRef.current 
      });
      return;
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      console.error('Cannot get 2D context');
      return;
    }

    const dims = niftiHeader.dims; // Get dimensions from header
    const nx = dims[1];
    const ny = dims[2];
    
    if (!nx || !ny) {
      console.error('Invalid dimensions:', { nx, ny });
      return;
    }

    // Match matplotlib imshow(data[:, :, z]): cols=ny (j), rows=nx (i)
    const displayWidth = ny;
    const displayHeight = nx;
    
    canvas.width = displayWidth;
    canvas.height = displayHeight;

    const imageData = ctx.createImageData(displayWidth, displayHeight);
    
    // Axial slice k in storage order: index = i + j*nx + k*nx*ny
    const sliceSize = nx * ny;
    const sliceOffset = currentSlice * sliceSize;

    if (sliceOffset + sliceSize > niftiImage.length) {
      console.error('Slice offset out of bounds:', { 
        sliceOffset, 
        sliceSize, 
        imageLength: niftiImage.length,
        currentSlice,
        totalSlices: dims[3]
      });
      return;
    }

    const sliceData = niftiImage.subarray(sliceOffset, sliceOffset + sliceSize);

    // Get min and max values for normalization
    let min = sliceData[0];
    let max = sliceData[0];
    
    for (let i = 0; i < sliceData.length; i++) {
      const value = sliceData[i];
      if (!isNaN(value) && isFinite(value)) {
        if (value < min) min = value;
        if (value > max) max = value;
      }
    }

    console.log('Data Range:', { min, max, sliceOffset, sliceSize, currentSlice });

    if (min === max || !isFinite(min) || !isFinite(max)) {
      console.error('Invalid data range:', { min, max });
      return;
    }

    // Apply window/level adjustments
    const windowMin = windowCenter - windowWidth / 2;
    const windowMax = windowCenter + windowWidth / 2;
    
    const windowRange = windowMax - windowMin;
    if (windowRange <= 0) {
      console.error('Invalid window range:', { windowMin, windowMax, windowWidth, windowCenter });
      return;
    }

    // pixel (col, row) ← voxel (i=row, j=col); assert ui[y,x] == data[y,x,z]
    for (let row = 0; row < displayHeight; row++) {
      for (let col = 0; col < displayWidth; col++) {
        let value = sliceData[row + col * nx];

        value = Math.max(windowMin, Math.min(windowMax, value));

        const normalizedValue = Math.floor(((value - windowMin) / windowRange) * 255);
        const pixelValue = Math.max(0, Math.min(255, normalizedValue));

        const pixelIndex = (row * displayWidth + col) * 4;

        imageData.data[pixelIndex] = pixelValue;     // R
        imageData.data[pixelIndex + 1] = pixelValue; // G
        imageData.data[pixelIndex + 2] = pixelValue; // B
        imageData.data[pixelIndex + 3] = 255;        // A
      }
    }

    ctx.putImageData(imageData, 0, 0);
    console.log('Slice rendered successfully (imshow orientation)');
  }, [niftiHeader, niftiImage, currentSlice, windowWidth, windowCenter]);

  // Render slice when dependencies change - include segmentationData so annotations are re-rendered
  useEffect(() => {
    if (niftiHeader && niftiImage) {
      console.log('[NiftiFileReader] Rendering slice', {
        currentSlice,
        hasSegmentationData: !!segmentationData[currentSlice],
        polygonCount: segmentationData[currentSlice]?.polygons?.length || 0
      });
      renderSlice();
    }
  }, [currentSlice, niftiImage, niftiHeader, windowWidth, windowCenter, segmentationData, renderSlice]);

  // Add this useEffect to automatically set initial window/level based on data range
  useEffect(() => {
    if (niftiImage && niftiHeader) {
      let min = niftiImage[0];
      let max = niftiImage[0];
      
      // Sample values to estimate range (for performance)
      const sampleStep = Math.max(1, Math.floor(niftiImage.length / 10000));
      for (let i = 0; i < niftiImage.length; i += sampleStep) {
        const value = niftiImage[i];
        if (!isNaN(value) && isFinite(value)) {
          if (value < min) min = value;
          if (value > max) max = value;
        }
      }
      
      console.log('Initial data range calculated:', { min, max });
      
      if (min !== max && isFinite(min) && isFinite(max)) {
        setDataRange({ min, max });
        const width = max - min;
        const center = min + width / 2;
        setWindowWidth(width);
        setWindowCenter(center);
        console.log('Window/level initialized:', { width, center });
      }
    }
  }, [niftiImage, niftiHeader]);

  // Sync external slice index with internal state
  useEffect(() => {
    if (externalSliceIndex !== undefined && externalSliceIndex !== null && externalSliceIndex !== currentSlice) {
      if (imageDimensions && externalSliceIndex >= 0 && externalSliceIndex < imageDimensions.totalSlices) {
        setCurrentSlice(externalSliceIndex);
      }
    }
  }, [externalSliceIndex, currentSlice, imageDimensions]);

  const handleSliceChange = (event: Event, newValue: number | number[]) => {
    setCurrentSlice(newValue as number);
  };

  const initializeSegmentationData = (sliceIndex: number) => {
    if (!segmentationData[sliceIndex]) {
      setSegmentationData(prev => ({
        ...prev,
        [sliceIndex]: {
          polygons: [],
          activePolygon: []
        }
      }));
    }
  };

  useEffect(() => {
    initializeSegmentationData(currentSlice);
  }, [currentSlice]);

  // const handlePolygonSelect = (polygonIndex: number) => {
  //   setSelectedPolygon(polygonIndex);
  // };

  const handleDeletePolygon = (polygonIndex: number) => {
    setSegmentationData(prev => {
      const currentSliceData = prev[currentSlice];
      const newPolygons = [...currentSliceData.polygons];
      newPolygons.splice(polygonIndex, 1);
      
      return {
        ...prev,
        [currentSlice]: {
          ...currentSliceData,
          polygons: newPolygons
        }
      };
    });
    setSelectedPolygon(null);
  };

  const handleEditPolygon = (polygonIndex: number) => {
    setIsEditing(true);
    setSelectedPolygon(polygonIndex);
  };

  // Add this helper function to check if a point is inside a polygon
  const isPointInPolygon = (point: Point, polygon: Point[]) => {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i].x, yi = polygon[i].y;
      const xj = polygon[j].x, yj = polygon[j].y;
      
      const intersect = ((yi > point.y) !== (yj > point.y))
          && (point.x < (xj - xi) * (point.y - yi) / (yj - yi) + xi);
      if (intersect) inside = !inside;
    }
    return inside;
  };

  // Canvas pointer → imshow / API pixel coords (same as display after orientation fix)
  const getCanvasCoordinates = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!segmentationCanvasRef.current) return null;
    
    const canvas = segmentationCanvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    const displayX = (e.clientX - rect.left) * (canvas.width / rect.width);
    const displayY = (e.clientY - rect.top) * (canvas.height / rect.height);

    return { x: displayX, y: displayY };
  };

  // Update the renderSegmentation function to always show points
  const renderSegmentation = useCallback(() => {
    if (!segmentationCanvasRef.current || !canvasRef.current) return;
    const ctx = segmentationCanvasRef.current.getContext('2d');
    if (!ctx) return;

    // Make sure segmentation canvas matches the image canvas dimensions
    segmentationCanvasRef.current.width = canvasRef.current.width;
    segmentationCanvasRef.current.height = canvasRef.current.height;

    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    
    const sliceData = segmentationData[currentSlice];
    if (!sliceData) return;

    const toDisplay = (point: Point): Point => niftiPointToDisplay(normalizePoint(point));

    // Draw completed polygons
    sliceData.polygons.forEach((polygon, index) => {
      if (polygon.points.length < 2) return;

      const displayPoints = polygon.points.map(toDisplay);
      
      // Draw filled polygon
      ctx.beginPath();
      ctx.moveTo(displayPoints[0].x, displayPoints[0].y);
      displayPoints.forEach((point, i) => {
        if (i > 0) ctx.lineTo(point.x, point.y);
      });
      ctx.closePath();
      
      const isSelected = !enableSam && index === selectedPolygon && isEditing;
      ctx.fillStyle = isSelected ? `${polygon.color}40` : `${polygon.color}20`;
      ctx.strokeStyle = isSelected ? '#FFFFFF' : polygon.color;
      ctx.lineWidth = isSelected ? 3 : 1.5;
      
      ctx.fill();
      ctx.stroke();

      // Draw vertices for all polygons (skip when SAM mode is on)
      if (!enableSam) {
      displayPoints.forEach((point) => {
        ctx.beginPath();
        ctx.arc(
          point.x, 
          point.y, 
          isSelected ? 5 : 3,
          0, 
          2 * Math.PI
        );
        ctx.fillStyle = isSelected ? '#FFFFFF' : polygon.color;
        ctx.strokeStyle = polygon.color;
        ctx.lineWidth = isSelected ? 2 : 1;
        ctx.fill();
        ctx.stroke();
      });
      }
    });

    const samPreviews = samPreviewBySlice[currentSlice] ?? [];
    samPreviews.forEach((polygon) => {
      if (polygon.points.length < 2) return;

      const displayPoints = polygon.points.map(toDisplay);

      ctx.beginPath();
      ctx.moveTo(displayPoints[0].x, displayPoints[0].y);
      displayPoints.forEach((point, i) => {
        if (i > 0) ctx.lineTo(point.x, point.y);
      });
      ctx.closePath();
      ctx.fillStyle = `${SAM_UNLABELED_COLOR}35`;
      ctx.strokeStyle = SAM_UNLABELED_COLOR;
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 4]);
      ctx.fill();
      ctx.stroke();
      ctx.setLineDash([]);
    });

    // Draw active polygon
    if (!enableSam && sliceData.activePolygon.length > 0) {
      const labelColor = labels.find(l => l.name === activeLabel)?.color || '#FF0000';
      const activeDisplay = sliceData.activePolygon.map(toDisplay);
      const cursorDisplay = lastCursorPosition.current
        ? toDisplay(lastCursorPosition.current)
        : null;
      
      // Draw lines between points
      ctx.beginPath();
      ctx.moveTo(activeDisplay[0].x, activeDisplay[0].y);
      activeDisplay.forEach((point, i) => {
        if (i > 0) ctx.lineTo(point.x, point.y);
      });
      
      // If polygon is closed or near first point, close the path
      if (isPolygonClosed || (cursorDisplay && sliceData.activePolygon.length > 2)) {
        const firstPoint = activeDisplay[0];
        if (cursorDisplay) {
          const distance = Math.hypot(
            firstPoint.x - cursorDisplay.x,
            firstPoint.y - cursorDisplay.y
          );
          
          if (distance < 10) {
            ctx.lineTo(firstPoint.x, firstPoint.y);
          } else {
            ctx.lineTo(cursorDisplay.x, cursorDisplay.y);
          }
        }
      }
      
      ctx.strokeStyle = labelColor;
      ctx.lineWidth = 2;
      ctx.stroke();

      // Draw vertices for active polygon
      activeDisplay.forEach((point, index) => {
        ctx.beginPath();
        ctx.arc(
          point.x, 
          point.y, 
          index === 0 ? 6 : 4, // Make first point larger
          0, 
          2 * Math.PI
        );
        ctx.fillStyle = index === 0 ? '#FFFFFF' : labelColor;
        ctx.strokeStyle = labelColor;
        ctx.lineWidth = index === 0 ? 2 : 1.5;
        ctx.fill();
        ctx.stroke();
      });
    }
  }, [segmentationData, currentSlice, selectedPolygon, isEditing, activeLabel, isPolygonClosed, labels, enableSam, samPreviewBySlice, niftiHeader]);

  // Update handleCanvasClick to not clear activePolygon until label is selected
  const handleCanvasClick = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const coords = getCanvasCoordinates(e);
    if (!coords) return;

    if (enableSam) {
      performSamSegmentation(coords);
      return;
    }

    const sliceData = segmentationData[currentSlice];
    if (!sliceData) return;

    // If in edit mode, exit only on click outside polygon and away from edges/vertices
    if (isEditing && selectedPolygon !== null) {
      if (suppressEditModeExitClickRef.current) {
        suppressEditModeExitClickRef.current = false;
        return;
      }

      const currentPolygon = sliceData.polygons[selectedPolygon].points;
      const staysInEdit =
        isPointInPolygon(coords, currentPolygon) ||
        isNearPolygonEdgesOrVertices(coords, currentPolygon);

      if (!staysInEdit) {
        setIsEditing(false);
        setSelectedPolygon(null);
        requestAnimationFrame(() => renderSegmentation());
      }
      return;
    }

    // Only proceed with polygon selection or drawing if not in edit mode
    if (!isEditing) {
      // Check if clicked inside any completed polygon
      for (let i = sliceData.polygons.length - 1; i >= 0; i--) {
        if (isPointInPolygon(coords, sliceData.polygons[i].points)) {
          setSelectedPolygon(i);
          setIsEditing(true);
          requestAnimationFrame(() => renderSegmentation());
          return;
        }
      }

      // Handle polygon drawing only if not in edit mode
      if (sliceData.activePolygon.length > 2) {
        const firstPoint = sliceData.activePolygon[0];
        const distance = Math.hypot(firstPoint.x - coords.x, firstPoint.y - coords.y);
        
        if (distance < 10) {
          // Just set the pending polygon without clearing the active polygon
          setPendingPolygon([...sliceData.activePolygon]);
          return;
        }
      }

      // Continue drawing
      setSegmentationData(prev => ({
        ...prev,
        [currentSlice]: {
          ...prev[currentSlice],
          activePolygon: [...(prev[currentSlice]?.activePolygon || []), coords]
        }
      }));
    }
  }, [segmentationData, currentSlice, isEditing, selectedPolygon, renderSegmentation, enableSam, performSamSegmentation]);

  // Add useEffect for rendering when edit mode changes or segmentation data changes
  useEffect(() => {
    if (niftiHeader && niftiImage) {
      console.log('[NiftiFileReader] Rendering segmentation', {
        currentSlice,
        hasSliceData: !!segmentationData[currentSlice],
        polygonCount: segmentationData[currentSlice]?.polygons?.length || 0
      });
      renderSegmentation();
    }
  }, [isEditing, selectedPolygon, segmentationData, currentSlice, niftiHeader, niftiImage, renderSegmentation]);

  const handleSelectedPointMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!segmentationCanvasRef.current || !selectedPoint) return;

    const coords = getCanvasCoordinates(e);
    if (!coords) return;

    // Update point position (NIfTI space)
    setSegmentationData(prev => {
      const newPolygons = [...prev[currentSlice].polygons];
      newPolygons[selectedPoint.polygonIndex].points[selectedPoint.pointIndex] = coords;
      return {
        ...prev,
        [currentSlice]: {
          ...prev[currentSlice],
          polygons: newPolygons
        }
      };
    });
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDragging || !segmentationCanvasRef.current || dragPoint === null || selectedPolygon === null) return;

    const coords = getCanvasCoordinates(e);
    if (!coords) return;

    // Update point position (NIfTI space)
    setSegmentationData(prev => {
      const currentSliceData = prev[currentSlice];
      const newPolygons = [...currentSliceData.polygons];
      const updatedPoints = [...newPolygons[selectedPolygon].points];
      updatedPoints[dragPoint.index] = coords;
      newPolygons[selectedPolygon] = {
        ...newPolygons[selectedPolygon],
        points: updatedPoints
      };

      return {
        ...prev,
        [currentSlice]: {
          ...currentSliceData,
          polygons: newPolygons
        }
      };
    });
  };

  // Add cursor position tracking for preview line
  const lastCursorPosition = useRef<Point | null>(null);

  // Modify handleContextMenu to handle both point deletion and label editing
  const handleContextMenu = (e: React.MouseEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    if (enableSam) return;
    
    if (!segmentationCanvasRef.current || !isEditing || selectedPolygon === null) return;
    
    const coords = getCanvasCoordinates(e);
    if (!coords) return;

    const sliceData = segmentationData[currentSlice];
    if (!sliceData?.polygons[selectedPolygon]) return;

    // Find if we clicked near any point
    const pointIndex = sliceData.polygons[selectedPolygon].points.findIndex((point: Point) => 
      Math.hypot(point.x - coords.x, point.y - coords.y) < VERTEX_HIT_RADIUS
    );

    if (pointIndex !== -1 && sliceData.polygons[selectedPolygon].points.length > 3) {
      // Handle point deletion
      setSegmentationData(prev => {
        const currentSliceData = prev[currentSlice];
        const newPolygons = [...currentSliceData.polygons];
        const updatedPoints = [...newPolygons[selectedPolygon].points];
        updatedPoints.splice(pointIndex, 1);
        newPolygons[selectedPolygon] = {
          ...newPolygons[selectedPolygon],
          points: updatedPoints
        };

        return {
          ...prev,
          [currentSlice]: {
            ...currentSliceData,
            polygons: newPolygons,
          }
        };
      });
    } else {
      // If not clicking on a point, show label edit dialog
      setEditingLabel({
        polygonIndex: selectedPolygon,
        position: { x: e.clientX, y: e.clientY }
      });
    }
  };

  // Update handleSegmentationCanvasMouseMove to use the new coordinate function
  const handleSegmentationCanvasMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const coords = getCanvasCoordinates(e);
    if (!coords || !segmentationCanvasRef.current) return;

    const canvas = segmentationCanvasRef.current;
    lastCursorPosition.current = coords;

    if (enableSam) {
      canvas.style.cursor = samLoading ? 'wait' : 'crosshair';
      renderSegmentation();
      return;
    }

    // Handle point dragging
    if (isDragging && dragPoint !== null && selectedPolygon !== null) {
      setSegmentationData(prev => {
        const currentSliceData = prev[currentSlice];
        const newPolygons = [...currentSliceData.polygons];
        const updatedPoints = [...newPolygons[selectedPolygon].points];
        updatedPoints[dragPoint.index] = coords;
        newPolygons[selectedPolygon] = {
          ...newPolygons[selectedPolygon],
          points: updatedPoints
        };

        return {
          ...prev,
          [currentSlice]: {
            ...currentSliceData,
            polygons: newPolygons
          }
        };
      });
      requestAnimationFrame(() => renderSegmentation());
      return;
    }

    // Handle cursor style changes
    const sliceData = segmentationData[currentSlice];

    // Edit mode point dragging/removal
    if (isEditing && selectedPolygon !== null && sliceData?.polygons[selectedPolygon]) {
      const points = sliceData.polygons[selectedPolygon].points;
      
      // Check if near any point
      const isNearPoint = points.some((point: Point) =>
        Math.hypot(point.x - coords.x, point.y - coords.y) < VERTEX_HIT_RADIUS
      );

      // Check if near any line segment
      let isNearLine = false;
      if (!isNearPoint) {
        for (let i = 0; i < points.length; i++) {
          const start = points[i];
          const end = points[(i + 1) % points.length];
          const { distance } = distanceToLineSegment(coords, start, end);
          if (distance < EDGE_HIT_THRESHOLD) {
            isNearLine = true;
            break;
          }
        }
      }

      if (isNearPoint) {
        canvas.style.cursor = isDragging ? 'grabbing' : 'grab';
        canvas.title = isDragging ? 'Release to drop point' : 'Drag to move point (Right-click to remove)';
      } else if (isNearLine) {
        canvas.style.cursor = 'crosshair';
        canvas.title = 'Click to add point';
      } else {
        canvas.style.cursor = 'default';
        canvas.title = isEditing ? 'Press Delete to remove polygon' : '';
      }
    }
    // Normal polygon drawing mode
    else if (sliceData?.activePolygon.length > 2) {
      const firstPoint = sliceData.activePolygon[0];
      const distance = Math.hypot(firstPoint.x - coords.x, firstPoint.y - coords.y);
      if (distance < 10) {
        canvas.style.cursor = 'pointer';
        canvas.title = 'Click to close polygon';
      } else {
        canvas.style.cursor = 'crosshair';
        canvas.title = '';
      }
    } else {
      canvas.style.cursor = 'crosshair';
      canvas.title = '';
    }
    
    renderSegmentation();
  }, [segmentationData, currentSlice, isEditing, selectedPolygon, isDragging, dragPoint, renderSegmentation, enableSam, samLoading]);

  const handleDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing || !segmentationCanvasRef.current) return;
    
    const canvas = segmentationCanvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left) * (canvas.width / rect.width);
    const y = (e.clientY - rect.top) * (canvas.height / rect.height);

    ctx.beginPath();
    if (lastPos.current) {
      ctx.moveTo(lastPos.current.x, lastPos.current.y);
      ctx.lineTo(x, y);
    } else {
      ctx.moveTo(x - 1, y);
      ctx.lineTo(x, y);
    }

    ctx.strokeStyle = tool === 'brush' ? 'rgba(255, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0)';
    ctx.lineWidth = brushSize;
    ctx.lineCap = 'round';
    ctx.stroke();

    lastPos.current = { x, y };
  };

  const PolygonList = () => {
    const sliceData = segmentationData[currentSlice];
    if (!sliceData?.polygons.length) return null;

    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="h6">Polygons</Typography>
        {sliceData.polygons.map((polygon, index) => (
          <Box 
            key={index}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              padding: 1,
              backgroundColor: selectedPolygon === index ? 'rgba(0,0,0,0.1)' : 'transparent'
            }}
          >
            <div style={{
              width: 16,
              height: 16,
              backgroundColor: polygon.color,
              borderRadius: '50%'
            }} />
            <Typography>{polygon.label}</Typography>
            <IconButton 
              size="small"
              onClick={() => handleDeletePolygon(index)}
            >
              <DeleteOutlineIcon />
            </IconButton>
          </Box>
        ))}
      </Box>
    );
  };

  // Add this function to remove the last point
  const removeLastPoint = () => {
    setSegmentationData(prev => {
      const currentSliceData = prev[currentSlice];
      if (!currentSliceData?.activePolygon.length) return prev;

      return {
        ...prev,
        [currentSlice]: {
          ...currentSliceData,
          activePolygon: currentSliceData.activePolygon.slice(0, -1),
        }
      };
    });
    
    // Reset polygon closed state if we remove a point
    setIsPolygonClosed(false);
  };

  // Update handleMouseDown to handle point dragging
  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (enableSam) return;

    const coords = getCanvasCoordinates(e);
    if (!coords || !isEditing || selectedPolygon === null) return;

    const sliceData = segmentationData[currentSlice];
    if (!sliceData?.polygons[selectedPolygon]) return;

    // First check if we clicked on any existing point
    const polygonPoints = sliceData.polygons[selectedPolygon].points;
    const pointIndex = polygonPoints.findIndex(point => 
      Math.hypot(point.x - coords.x, point.y - coords.y) < VERTEX_HIT_RADIUS
    );

    if (pointIndex !== -1) {
      suppressEditModeExitClickRef.current = true;
      setIsDragging(true);
      setDragPoint({
        index: pointIndex,
        initialPos: polygonPoints[pointIndex]
      });
      return;
    }

    // If not clicking on a point, check if clicking near a line segment
    let minDistance = Infinity;
    let insertIndex = -1;
    let nearestPoint: Point | null = null;

    // Check each line segment
    for (let i = 0; i < polygonPoints.length; i++) {
      const start = polygonPoints[i];
      const end = polygonPoints[(i + 1) % polygonPoints.length];
      const { distance, point } = distanceToLineSegment(coords, start, end);

      if (distance < EDGE_HIT_THRESHOLD && distance < minDistance) {
        minDistance = distance;
        insertIndex = i + 1;
        nearestPoint = point;
      }
    }

    // If we found a close line segment, insert a new point
    if (insertIndex !== -1 && nearestPoint) {
      const newPoint: Point = { x: nearestPoint.x, y: nearestPoint.y };
      
      setSegmentationData(prev => {
        const currentSliceData = prev[currentSlice];
        const newPolygons = [...currentSliceData.polygons];
        const updatedPoints = [...newPolygons[selectedPolygon].points];
        updatedPoints.splice(insertIndex, 0, newPoint);
        newPolygons[selectedPolygon] = {
          ...newPolygons[selectedPolygon],
          points: updatedPoints
        };

        return {
          ...prev,
          [currentSlice]: {
            ...currentSliceData,
            polygons: newPolygons,
          }
        };
      });

      // Start dragging the newly inserted point
      suppressEditModeExitClickRef.current = true;
      setIsDragging(true);
      setDragPoint({
        index: insertIndex,
        initialPos: newPoint
      });
    }
  };

  // Update handleMouseUp to clean up after dragging
  const handleMouseUp = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (isDragging && dragPoint !== null && selectedPolygon !== null) {
      const coords = getCanvasCoordinates(e);
      if (coords) {
        // Final update of the point position
        setSegmentationData(prev => {
          const currentSliceData = prev[currentSlice];
          const newPolygons = [...currentSliceData.polygons];
          const updatedPoints = [...newPolygons[selectedPolygon].points];
          updatedPoints[dragPoint.index] = coords;
          newPolygons[selectedPolygon] = {
            ...newPolygons[selectedPolygon],
            points: updatedPoints
          };

          return {
            ...prev,
            [currentSlice]: {
              ...currentSliceData,
              polygons: newPolygons
            }
          };
        });
      }
    }
    
    setIsDragging(false);
    setDragPoint(null);
    requestAnimationFrame(() => renderSegmentation());
  };

  // Add this useEffect for handling delete key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (enableSam) return;

      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        
        // Delete polygon in edit mode
        if (isEditing && selectedPolygon !== null) {
          setSegmentationData(prev => {
            const currentSliceData = prev[currentSlice];
            const newPolygons = [...currentSliceData.polygons];
            newPolygons.splice(selectedPolygon, 1);

            return {
              ...prev,
              [currentSlice]: {
                ...currentSliceData,
                polygons: newPolygons
              }
            };
          });
          setIsEditing(false);
          setSelectedPolygon(null);
        }
        // Delete last point while drawing polygon
        else if (segmentationData[currentSlice]?.activePolygon.length > 0) {
          setSegmentationData(prev => {
            const currentSliceData = prev[currentSlice];
            return {
              ...prev,
              [currentSlice]: {
                ...currentSliceData,
                activePolygon: currentSliceData.activePolygon.slice(0, -1)
              }
            };
          });
          setIsPolygonClosed(false);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isEditing, selectedPolygon, currentSlice, segmentationData, enableSam]);

  // Update handleLabelSelect to clear the active polygon only after creating the new polygon
  const handleLabelSelect = (label: string) => {
    if (pendingPolygon) {
      const newPolygon: Polygon = {
        points: pendingPolygon,
        label: label,
        color: labels.find(l => l.name === label)?.color || '#FF0000'
      };

      setSegmentationData(prev => ({
        ...prev,
        [currentSlice]: {
          ...prev[currentSlice],
          polygons: [...prev[currentSlice].polygons, newPolygon],
          // Clear the active polygon only after creating the new polygon
          activePolygon: []
        }
      }));

      setPendingPolygon(null);
      setIsPolygonClosed(false);
    }
    setActiveLabel(label);
  };

  // Add this component for the label selection dialog
  const SamLabelQueueDialog = () => {
    if (labelQueueIndices.length === 0) return null;

    const maskNumber = labelQueuePosition + 1;
    const totalMasks = labelQueueIndices.length;

    return (
      <Paper
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          p: 2,
          zIndex: 1000,
          backgroundColor: 'white',
          boxShadow: 3,
          minWidth: 200,
        }}
      >
        <Typography gutterBottom>
          Select a label for SAM mask {maskNumber} of {totalMasks}:
        </Typography>
        <Box sx={LABEL_LIST_SX}>
          {labels.map((label, index) => (
            <Button
              key={`${label.name}-${index}`}
              onClick={() => handleSamLabelQueueSelect(label.name)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                justifyContent: 'flex-start',
              }}
            >
              <div
                style={{
                  width: 16,
                  height: 16,
                  backgroundColor: label.color,
                  borderRadius: '50%',
                }}
              />
              {label.name}
            </Button>
          ))}
        </Box>
      </Paper>
    );
  };

  const LabelSelectionDialog = () => {
    if (!pendingPolygon) return null;

    return (
      <Paper
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          p: 2,
          zIndex: 1000,
          backgroundColor: 'white',
          boxShadow: 3,
          minWidth: 200,
        }}
      >
        <Typography gutterBottom>Select a label for the polygon:</Typography>
        <Box sx={LABEL_LIST_SX}>
          {labels.map((label, index) => (
            <Button
              key={`${label.name}-${index}`}
              onClick={() => handleLabelSelect(label.name)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                justifyContent: 'flex-start'
              }}
            >
              <div style={{
                width: 16,
                height: 16,
                backgroundColor: label.color,
                borderRadius: '50%'
              }} />
              {label.name}
            </Button>
          ))}
        </Box>
      </Paper>
    );
  };

  // Add this component for the label editing dialog
  const LabelEditDialog = memo(() => {
    if (!editingLabel) return null;

    return (
      <Paper
        ref={dialogRef}
        sx={{
          position: 'fixed',
          left: editingLabel.position.x,
          top: editingLabel.position.y,
          p: 2,
          zIndex: 1000,
          backgroundColor: 'white',
          boxShadow: 3,
          minWidth: 200,
        }}
      >
        <Typography gutterBottom>Change label:</Typography>
        <Box sx={LABEL_LIST_SX}>
          {labels.map((label, index) => (
            <Button
              key={`${label.name}-${index}`}
              onClick={() => {
                setSegmentationData(prev => {
                  const currentSliceData = prev[currentSlice];
                  const newPolygons = [...currentSliceData.polygons];
                  newPolygons[editingLabel.polygonIndex] = {
                    ...newPolygons[editingLabel.polygonIndex],
                    label: label.name,
                    color: label.color
                  };

                  return {
                    ...prev,
                    [currentSlice]: {
                      ...currentSliceData,
                      polygons: newPolygons
                    }
                  };
                });
                setEditingLabel(null);
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                justifyContent: 'flex-start'
              }}
            >
              <div style={{
                width: 16,
                height: 16,
                backgroundColor: label.color,
                borderRadius: '50%'
              }} />
              {label.name}
            </Button>
          ))}
        </Box>
      </Paper>
    );
  });

  // Add click handler to close the label edit dialog when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isEditing && 
          segmentationCanvasRef.current && 
          !segmentationCanvasRef.current.contains(event.target as Node) &&
          (!dialogRef.current || !dialogRef.current.contains(event.target as Node))) {
        setIsEditing(false);
        setSelectedPolygon(null);
        setEditingLabel(null);
      }
    };

    if (isEditing) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isEditing]);

  // Also add loading state if not already present
  const [loading, setLoading] = useState(false);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {error && (
        <Box sx={{ 
          padding: '16px', 
          margin: '16px',
          backgroundColor: '#ffebee',
          borderRadius: '8px',
          borderLeft: '4px solid #f44336'
        }}>
          <Typography color="error" variant="h6" sx={{ marginBottom: '8px', fontWeight: 600 }}>
            Error Loading NIFTI File
          </Typography>
          <Typography color="error" variant="body2">
            {error}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', marginTop: '8px', color: '#666' }}>
            Please check the browser console for more details.
          </Typography>
        </Box>
      )}

      {error && (
        <Box sx={{ 
          padding: '16px', 
          margin: '16px',
          backgroundColor: '#ffebee',
          borderRadius: '8px',
          borderLeft: '4px solid #f44336'
        }}>
          <Typography color="error" variant="h6" sx={{ marginBottom: '8px', fontWeight: 600 }}>
            Error Loading NIFTI File
          </Typography>
          <Typography color="error" variant="body2">
            {error}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', marginTop: '8px', color: '#666' }}>
            Please check the browser console for more details.
          </Typography>
        </Box>
      )}

      {niftiImage && niftiHeader && imageDimensions && (
        <ViewerContainer elevation={0}>
          <ControlsContainer>
            <div className="control-group">
              <Typography>Window Width</Typography>
              <StyledSlider
                value={windowWidth}
                onChange={(_, value) => {
                  setWindowWidth(value as number);
                }}
                min={1}
                max={dataRange ? dataRange.max - dataRange.min : 500}
                valueLabelDisplay="auto"
              />
            </div>
            
            <div className="control-group">
              <Typography>Window Center</Typography>
              <StyledSlider
                value={windowCenter}
                onChange={(_, value) => {
                  setWindowCenter(value as number);
                }}
                min={dataRange?.min ?? 0}
                max={dataRange?.max ?? 255}
                valueLabelDisplay="auto"
              />
            </div>

            {showSamToggle && (
              <FormControlLabel
                control={
                  <Switch
                    checked={enableSam}
                    onChange={handleSamToggle}
                    disabled={!projectId || !imageId || samToggleLoading}
                    color="primary"
                  />
                }
                label="Enable SAM"
              />
            )}
          </ControlsContainer>

          <ViewerWrapper>
            <CanvasContainer>
              <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
              <canvas
                ref={canvasRef}
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  zIndex: 1,
                  display: 'block',
                  maxWidth: '100%',
                  maxHeight: '100%',
                  imageRendering: 'pixelated'
                }}
              />
              <canvas
                ref={segmentationCanvasRef}
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  pointerEvents: 'all',
                  zIndex: 2,
                  display: 'block'
                }}
                onClick={handleCanvasClick}
                onContextMenu={handleContextMenu}
                onMouseDown={handleMouseDown}
                onMouseMove={handleSegmentationCanvasMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
              />
              {showSamToggle && samLoading && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255,255,255,0.4)',
                    zIndex: 3,
                  }}
                >
                  <CircularProgress size={40} />
                </Box>
              )}
              </Box>
            </CanvasContainer>

            <SliceControls>
              <Typography sx={{ minWidth: 'max-content' }}>
                Slice: {currentSlice + 1} / {niftiHeader.dims[3]}
              </Typography>
              <StyledSlider
                value={currentSlice + 1}
                onChange={handleSliceChange}
                min={0}
                max={niftiHeader.dims[3] - 1}
                valueLabelDisplay="auto"
              />
              {!hideSaveButton && (
              <SaveButton
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={saveSegmentation}
              >
                Save
              </SaveButton>
              )}
            </SliceControls>
          </ViewerWrapper>
          
          <LabelSelectionDialog />
          <SamLabelQueueDialog />
          <LabelEditDialog />

          {showSamToggle && (
            <Dialog
              open={samInitDialogOpen}
              onClose={(_, reason) => {
                if (reason === 'backdropClick') return;
                closeSamInitDialog();
              }}
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle>Initializing SAM</DialogTitle>
              <DialogContent>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {samInitMessage}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  We are initializing SAM for this volume. Please wait.
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, Math.max(0, samInitPercentage))}
                  sx={{ mb: 1 }}
                />
                <Typography variant="body2" color="text.secondary">
                  {Math.round(samInitPercentage)}% complete
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={closeSamInitDialog} color="primary">
                  Okay, come back later
                </Button>
              </DialogActions>
            </Dialog>
          )}
        </ViewerContainer>
      )}

      {!niftiImage && !error && (
        <WelcomeContainer>
          <WelcomeContent>
            <WelcomeIcon />
            <WelcomeTitle variant="h4">Welcome to NIFTI Viewer</WelcomeTitle>
            <WelcomeText variant="body1">
              Select an image from the list on the left to start viewing and analyzing NIFTI files. 
              The viewer supports various visualization options and tools for detailed analysis.
            </WelcomeText>
          </WelcomeContent>
        </WelcomeContainer>
      )}
    </Box>
  );
}

export default NiftiFileReader;